import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useEffect, useState } from "react";
import useApi from "../../../hooks/useGaneshApi";
import { useAuth } from "../../../contexts/AuthContext";

dayjs.extend(relativeTime);

interface Props {
  chainId: string;
}

interface JobStatus {
  job_id: number;
  status: "QUEUED" | "IN_PROGRESS" | "COMPLETED" | "FAILED";
  updated_at: string;
}

export const TriggerBrandRefreshButton: React.FC<Props> = ({ chainId }) => {
  const [showStatus, setShowStatus] = useState(false);
  const { apiRequest } = useApi();
  const { authAttributes } = useAuth();
  const adminId = authAttributes?.id;

  const {
    data: jobStatus,
    isLoading,
    error,
    refetch,
  } = useQuery<JobStatus | null, Error>({
    queryKey: ["brand-refresh-status", chainId],
    queryFn: async () => {
      const params = new URLSearchParams({ chain_id: chainId });
      const response = await apiRequest<JobStatus>(
        `/v1/server-jobs/status/brand-refresh?${params.toString()}`,
        {
          method: "GET",
        }
      );
      return response.data;
    },
    retry: 5,
    refetchInterval: 1000 * 60 * 5, // 5 minutes
  });

  const {
    mutate: postTriggerRefresh,
    isPending: isTriggering,
    isError: isTriggeringError,
  } = useMutation({
    mutationFn: async (payload: { chain_id: string; admin_id: number }) => {
      const response = await apiRequest(
        "/v1/server-jobs/trigger/brand-refresh",
        {
          method: "POST",
          body: payload,
        }
      );
      return response as any;
    },
    onSuccess: () => {
      refetch(); // Refresh job status after triggering
    },
    onError: (error) => {
      console.error("Failed to trigger brand refresh:", error);
    },
  });

  const handleTriggerClick = () => {
    if (!adminId) {
      console.error("Missing admin_id from AuthContext.");
      return;
    }

    postTriggerRefresh({ chain_id: chainId, admin_id: adminId });
  };

  useEffect(() => {
    if (jobStatus?.updated_at) {
      const lastUpdated = dayjs(jobStatus.updated_at);
      const diffInHours = dayjs().diff(lastUpdated, "hour");
      setShowStatus(diffInHours < 24);
    }
  }, [jobStatus]);

  const isBusy =
    jobStatus?.status === "QUEUED" || jobStatus?.status === "IN_PROGRESS";
  const buttonDisabled = isLoading || isBusy || isTriggering;

  return (
    <Stack spacing={1}>
      <Button
        variant="contained"
        color="primary"
        disabled={buttonDisabled}
        onClick={handleTriggerClick}
      >
        {isTriggering ? (
          <CircularProgress size={20} color="inherit" />
        ) : isBusy ? (
          "Brand Refresh In Progress"
        ) : (
          "Trigger Brand Refresh"
        )}
      </Button>

      {showStatus && jobStatus?.status && (
        <Typography variant="body2" color="textSecondary">
          Last job: {jobStatus.status} • {dayjs(jobStatus.updated_at).fromNow()}
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error">
          Failed to load brand refresh status.
        </Typography>
      )}

      {isTriggeringError && (
        <Typography variant="body2" color="error">
          Error triggering brand refresh.
        </Typography>
      )}
    </Stack>
  );
};
